/**
 * aesKey + sessionKeyId 预生成池
 * 密钥池管理
 */
import { getSecureSessionId } from '@/api/sys/tools';

interface SecureSessionItem {
    aesKey: CryptoKey;
    sessionKeyId: string;
    // 创建时间戳
    createdAt: number;
    used: boolean;
}

// 固定RSA公钥
const PUBLIC_KEY_PEM = `
-----BEGIN PUBLIC KEY-----
MIICIjANBgkqhkiG9w0BAQEFAAOCAg8AMIICCgKCAgEApV9KjaqHoYX74o06zATR
oAkomD2NlYOCYGkOYkdP6f/wxm4BHteE3XUGeMjcB8iQXsQiPxMQsVCqqZTR4dmo
WD0IBe7AyJGFBD/6dhM28ZEBOxppH9bHuj36a9cTnze9SgpmhNByHmyllW0cVtY3
yK68puJkWD++TuIULUgU5o6H/GAGsCFI4aYFLFaN6+UW1KUeFsxb1YaUh1YtBIqx
nd8H6zQbluDskgt13bhKdByBjV97CJq11Dm1XNBax3Uu3fxVEfqrdhXhD2U+s6x9
iloTGTUQciderEPE1f9nGEskOJzQEx6LRQSNZhJKQtT2wmKLbNDLOgsFzWyzSwOE
y0q1PMjx2hGUmRToVJhf/e001k9+dI+DlyFIqtjep/2YonEhHsVv/IKIKuIruTsM
WkENfENCEBdoquCTxpX2yvAy8dShTC2gPcsh3vwdcVlmmPrQE3h7CCoQSh/QyzI0
aB3sFAkDqPfzVbBVpF1crN8KWHEaqxub+OTWvFi6jknkEB0ZKn0B1S+teZS/z3/c
MwZ6/lswsAwWdeLda4VHza54LXPi5L8ZJkZ+8Adrwn6puZ4bj4kjVbQyD0L6KZlY
yDhl5jzgX/Bk4QSMq8H4WPcy77dJRsh9zcH9pOw48hdluAv5wkVfblNAKXTypqQI
MGYrR+l+uklDn5Z1hhQpPLUCAwEAAQ==
-----END PUBLIC KEY-----
`;

// 密钥池配置
// 常驻 1 个可用密钥
const POOL_SIZE = 1;

// 剩余可用密钥少于 2 个时，进行补充
const MIN_AVAILABLE_THRESHOLD = 1;

// 密钥生命有效期（后端存活）
const SERVER_KEY_TTL = 5 * 60 * 1000;

// 密钥生命前端有效期（前端保守判断，）
const KEY_TTL = 4 * 60 * 1000;

// 密钥过期前刷新时间（提前量）
const PREEMPTIVE_SECONDS = 45 * 1000;

class SecureSessionManager {
    // 密钥池
    private pool: SecureSessionItem[] = [];

    // 定时器 ID
    private timerId: number | null = null;

    // 是否已初始化
    private hasEverInited = false;

    // 进行中的补充任务；并发调用共用同一个 Promise，保证所有调用方都等待补充完成。
    private refillPromise: Promise<void> | null = null;

    // constructor() {
    //     this.init().catch(console.error);
    // }

    /**
     * 初始化（创建密钥池 + 启动定时更新、恢复定时更新）
     * @public
     */
    public async init() {
        // 未初始化或可用数量大于等于最低数量
        if (this.hasEverInited && this.getAvailableCount() >= MIN_AVAILABLE_THRESHOLD) {
            // 恢复定时刷新
            this.scheduleNextRefresh();
            return;
        }

        console.log('[SecureSession] 正在初始化密钥池...');
        await this.refillPool();
        this.hasEverInited = true;

        // 开启定时刷新
        this.scheduleNextRefresh();
    }

    /**
     * 停止定时器
     * @public
     */
    public destroy() {
        if (this.timerId !== null) {
            clearTimeout(this.timerId);
            this.timerId = null;
            console.log('[SecureSession] 定时刷新已停止');
        }
    }

    /**
     * 对外方法：获取可用密
     * 使用完后即使补充
     */
    public async getAvailableSession(): Promise<SecureSessionItem> {
        if (!this.hasEverInited) {
            await this.init();
        }

        const now = Date.now();
        let available = this.findAvailableSession(now);

        // 池子枯竭，无可用密钥
        if (!available) {
            console.warn('[SecureSession] 密钥池已空，同步紧急补充...');
            await this.refillPool();
            available = this.findAvailableSession() as SecureSessionItem;
        }

        // 正常命中：标记为已用
        // available.used = true;

        // 检查可用数量
        this.checkAndTriggerBackgroundRefill(now);

        return available;
    }

    // ======================== 内部工具方法 ========================
    /**
     * 获取当前可用密钥信息
     * @private
     */
    private findAvailableSession(now: number = Date.now()): SecureSessionItem | undefined {
        return this.pool.find((s) => !s.used && now - s.createdAt < KEY_TTL);
    }

    /**
     * 获取当前可用密钥数量
     * @private
     */
    private getAvailableCount(now: number = Date.now()): number {
        return this.pool.filter((s) => !s.used && now - s.createdAt < KEY_TTL).length;
    }

    /**
     * 检查密钥池可用密钥数量，少于阀值时进行补充
     * @param now
     * @private
     */
    private checkAndTriggerBackgroundRefill(now: number = Date.now()) {
        // 可用数量
        const availableCount = this.getAvailableCount(now);

        if (availableCount < MIN_AVAILABLE_THRESHOLD && !this.refillPromise) {
            console.log(`[SecureSession] 剩余可用密钥 ${availableCount} 个，低于阈值 ${MIN_AVAILABLE_THRESHOLD}，后台补货中...`);
            this.refillPool().then(() => {
                // 重新定时
                this.scheduleNextRefresh();
            }).catch((err) => {
                console.error('[SecureSession] 后台补货失败', err);
            });
        }
    }

    /**
     * 定时更新，保证随时有可用密钥
     * @private
     */
    private scheduleNextRefresh(): void {
        if (this.timerId !== null) {
            clearTimeout(this.timerId);
            this.timerId = null;
        }

        if (this.pool.length === 0) {
            return;
        }

        const now = Date.now();
        // 最旧的密钥
        const oldest = this.pool.reduce((o, s) => (s.createdAt < o.createdAt ? s : o));

        const age = now - oldest.createdAt;
        const timeLeft = SERVER_KEY_TTL - age;
        const nextIn = Math.max(1000, timeLeft - 45 * 1000);
        console.log(`[SecureSession] 下次刷新倒计时: ${nextIn / 1000} 秒`);

        this.timerId = setTimeout(() => {
            console.log('[SecureSession] 定时触发：密钥即将过期，提前刷新');
            this.refillPool().then(() => {
                this.scheduleNextRefresh();
            }).catch(console.error);
        }, nextIn) as unknown as number;
    }

    /**
     * 补充密钥池；并发请求复用进行中的任务，避免调用方在补充完成前读取空池。
     */
    private refillPool(): Promise<void> {
        if (this.refillPromise) {
            return this.refillPromise;
        }

        this.refillPromise = this.doRefillPool().finally(() => {
            this.refillPromise = null;
        });

        return this.refillPromise;
    }

    /**
     * 批量生成密钥并换取 sessionKeyId。
     */
    private async doRefillPool(): Promise<void> {
        try {
            const need = POOL_SIZE - this.getAvailableCount();
            if (need <= 0) {
                console.log('[SecureSession] 密钥充足，无需补充');
                return;
            }

            console.log(`[SecureSession] 正在补充 ${need} 个密钥（单个接口）...`);

            const newSessions: SecureSessionItem[] = [];

            for (let i = 0; i < need; i += 1) {
                console.log('[SecureSession] 开始补充密钥', i);
                // eslint-disable-next-line no-await-in-loop
                const { aesKey, encryptedBase64 } = await this.generateEncryptedAESKey();

                // eslint-disable-next-line no-await-in-loop
                const res = await getSecureSessionId({ encrypted_session_key: encryptedBase64 });
                const sessionKeyId = res.data.data.encryption_session_id;

                if (!sessionKeyId) throw new Error('后端未返回 sessionKeyId');

                newSessions.push({
                    aesKey,
                    sessionKeyId,
                    createdAt: Date.now(),
                    used: false,
                });

                // 保留未使用的旧密钥 + 添加新的
                this.pool = [
                    ...this.pool.filter((s) => !s.used),
                    ...newSessions,
                ].slice(-POOL_SIZE);
                console.log('[SecureSession] 密钥池', this.pool);
            }
        } catch (e) {
            console.error('[SecureSession] 密钥池刷新失败', e);
            throw e;
        }
    }

    /**
     * 生成一个 AES-256-CBC 密钥并用 RSA 公钥加密
     * @private
     */
    private async generateEncryptedAESKey(): Promise<{
        aesKey: CryptoKey;
        encryptedBase64: string;
    }> {
        // 生成 AES-256-CBC 密钥
        console.log('生成密钥');
        const aesKey = await window.crypto.subtle.generateKey(
            { name: 'AES-CBC', length: 256 },
            true,
            ['encrypt'],
        );

        // 导出原始密钥
        const rawKey = await window.crypto.subtle.exportKey('raw', aesKey);

        // RSA-OAEP 加密
        const encrypted = await this.rsaEncrypt(rawKey);

        return {
            aesKey,
            encryptedBase64: this.arrayBufferToBase64(encrypted),
        };
    }

    /**
     * RSA-OAEP 加密（SHA-256）
     */
    private async rsaEncrypt(data: ArrayBuffer): Promise<ArrayBuffer> {
        const publicKey = await window.crypto.subtle.importKey(
            'spki',
            this.pemToArrayBuffer(PUBLIC_KEY_PEM),
            { name: 'RSA-OAEP', hash: 'SHA-1' },
            false,
            ['encrypt'],
        );
        return crypto.subtle.encrypt({ name: 'RSA-OAEP' }, publicKey, data);
    }

    /**
     * PEM → ArrayBuffer
     */
    private pemToArrayBuffer(pem: string): ArrayBuffer {
        const b64 = pem
            .replace(/-----BEGIN PUBLIC KEY-----|-----END PUBLIC KEY-----|\s/g, '');

        const binary = window.atob(b64);
        const buf = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i += 1) {
            buf[i] = binary.charCodeAt(i);
        }

        return buf.buffer;
    }

    /** ArrayBuffer → Base64 */
    private arrayBufferToBase64(buffer: ArrayBuffer): string {
        return window.btoa(String.fromCharCode(...new Uint8Array(buffer)));
    }
}

// eslint-disable-next-line import/prefer-default-export
export const secureSession = new SecureSessionManager();
